import React, { useEffect, useState } from 'react';
import { Box, Text, Flex, Icon, Button, Link, Spinner, useToast } from '@chakra-ui/react';
import { FaDownload, FaExternalLinkAlt } from 'react-icons/fa';
import { ref, getDownloadURL } from 'firebase/storage';
import { storage } from '../../../firebase';
import { isVideoUrl, downloadFile, getDownloadFileName } from 'utils/fileUtils';
import { ff } from '../../../theme';
import { StatusPill } from './submissionSheetParts';

interface ApprovedContentPreviewProps {
  /**
   * The approved asset. Non-UGC drafts store a full storage URL, UGC content
   * units store a bare storage path — both are accepted.
   */
  url: string;
  /** Section heading, e.g. 'Approved content' or 'Approved draft'. */
  label?: string;
  /** Used to name the downloaded file when the URL has no usable filename. */
  fileNameBase?: string;
  /** Line above the download button. Pass null to hide it (e.g. archive views). */
  instruction?: string | null;
}

/**
 * Shows an approved asset with a download button so the influencer can save the
 * video/photo to their device. Used by the UGC and non-UGC post-link submission
 * sheets before posting, and to review finished work afterwards.
 */
const ApprovedContentPreview: React.FC<ApprovedContentPreviewProps> = ({
  url,
  label = 'Approved content',
  fileNameBase = 'foodfluence-content',
  instruction,
}) => {
  const toast = useToast();
  const [isDownloading, setIsDownloading] = useState(false);
  const [didDownloadFail, setDidDownloadFail] = useState(false);
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(null);
  const [didResolveFail, setDidResolveFail] = useState(false);
  // Detect from the original value: it always carries the real file extension,
  // while a resolved download URL may not.
  const isVideo = isVideoUrl(url);

  // UGC units store a storage path rather than a URL, so resolve those to a
  // download URL before rendering. Full URLs are used as-is.
  useEffect(() => {
    let isStale = false;
    setResolvedUrl(null);
    setDidResolveFail(false);

    if (/^https?:\/\//i.test(url)) {
      setResolvedUrl(url);
      return;
    }

    getDownloadURL(ref(storage, url))
      .then(downloadUrl => {
        if (!isStale) setResolvedUrl(downloadUrl);
      })
      .catch(error => {
        console.error('Error resolving approved content URL:', error);
        if (!isStale) setDidResolveFail(true);
      });

    return () => { isStale = true; };
  }, [url]);

  const handleDownload = async () => {
    if (!resolvedUrl) return;
    setIsDownloading(true);
    const succeeded = await downloadFile(resolvedUrl, getDownloadFileName(url, fileNameBase));
    setIsDownloading(false);
    setDidDownloadFail(!succeeded);

    if (succeeded) {
      toast({
        title: `${isVideo ? 'Video' : 'Photo'} downloaded`,
        description: 'Check your downloads, then post it from your device.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    toast({
      title: 'Download blocked',
      description: 'Open the content in a new tab, then press and hold to save it.',
      status: 'warning',
      duration: 5000,
      isClosable: true,
    });
  };

  return (
    <Box fontFamily="display" color={ff.ink}>
      <Flex align="center" justify="space-between" mb="10px" gap="12px">
        <Text fontSize="12px" fontWeight={600} color="#55555A" textTransform="uppercase" letterSpacing="0.08em">
          {label}
        </Text>
        <StatusPill tone="green">Approved</StatusPill>
      </Flex>

      <Box
        borderRadius="18px"
        overflow="hidden"
        bg={ff.ink}
        mb="10px"
        minH={resolvedUrl ? undefined : '140px'}
        display="flex"
        alignItems="center"
        justifyContent="center"
      >
        {didResolveFail ? (
          <Text fontSize="14px" color={ff.faint} py={8} px={4} textAlign="center">
            Couldn't load this content. Try again later.
          </Text>
        ) : !resolvedUrl ? (
          <Spinner size="md" color={ff.muted} thickness="2px" />
        ) : isVideo ? (
          <video
            src={resolvedUrl}
            controls
            playsInline
            preload="metadata"
            style={{ width: '100%', maxHeight: '280px', display: 'block', objectFit: 'contain' }}
          />
        ) : (
          <img
            src={resolvedUrl}
            alt={label}
            style={{ width: '100%', maxHeight: '280px', display: 'block', objectFit: 'contain' }}
          />
        )}
      </Box>

      {instruction !== null && (
        <Text fontSize="13px" lineHeight={1.4} color="#55555A" mb="10px">
          {instruction ??
            `Download this ${isVideo ? 'video' : 'photo'} and post it, then paste the live link below.`}
        </Text>
      )}

      <Button
        onClick={handleDownload}
        isDisabled={!resolvedUrl}
        isLoading={isDownloading}
        loadingText="Downloading"
        w="full"
        h="48px"
        borderRadius="full"
        bg="white"
        border={`1.5px solid ${ff.ink}`}
        color={ff.ink}
        fontWeight={600}
        fontSize="15px"
        leftIcon={<Icon as={FaDownload} boxSize={3.5} />}
        _hover={{ bg: 'white' }}
        _active={{ bg: ff.grey }}
      >
        Download {isVideo ? 'video' : 'photo'}
      </Button>

      {didDownloadFail && resolvedUrl && (
        <Link
          href={resolvedUrl}
          isExternal
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={1.5}
          mt={2}
          minH="44px"
          fontSize="14px"
          color={ff.ink}
          fontWeight={600}
          textDecoration="underline"
        >
          Open in a new tab
          <Icon as={FaExternalLinkAlt} boxSize={2.5} />
        </Link>
      )}
    </Box>
  );
};

export default ApprovedContentPreview;
